import React, { useCallback, useContext, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import get from 'lodash-es/get';

import { FjCheckbox, fjModalSizes } from 'components/primitives';
import { LayoutContext } from 'components/layout/layout-context';
import { selectCurrentUser, selectGDPRResult } from 'selectors';
import { Modal } from 'components/styled-primitives';
import { AuthClientUtils } from 'ducks/auth-client';
import { GDPRActions } from 'actions';
import { navigate } from 'utils';
import { useAppDispatch } from 'store/hooks';

interface ChecksState {
  personalData: boolean;
  requestForget: boolean;
  portData: boolean;
}

type CheckName = keyof ChecksState;

interface ConfirmationModal {
  heading: string;
  callback: () => void;
}

// Shape of the `gdpr` slice read via selectGDPRResult (which resolves as `unknown`).
interface GDPRResult {
  open?: boolean;
  success?: boolean;
  result?: React.ReactNode;
}

const Settings = (): React.JSX.Element => {
  const currentUser = useSelector(selectCurrentUser);
  const gdprResult = useSelector(selectGDPRResult) as GDPRResult;
  const dispatch = useAppDispatch();
  const store = useStore();
  const { confirm } = useContext(LayoutContext);

  // `personalData` mirrors trackingId presence at mount time. The original class
  // also had a `getDerivedStateFromProps` that was a no-op (its guard fired only
  // when state already equaled the next value), so the initializer alone is a
  // faithful translation. See docs/react19-step1-stage-1b-batch2-settings-eula.md.
  const [checks, setChecks] = useState<ChecksState>({
    personalData: !!currentUser.trackingId,
    requestForget: false,
    portData: false,
  });

  const setupConfirmationModal = useCallback(
    (name: CheckName, checked: boolean): ConfirmationModal => {
      const { optOutDate, id: userId } = currentUser;

      const hash = {
        personalData: {
          heading: {
            checked: 'PLACEHOLDER STORE DATA',
            unchecked: 'PLACEHOLDER STOP STORING DATA',
          },
          callback: {
            // @TODO wire in callback to start storing user data
            checked: () => {
              dispatch(GDPRActions.updatePersonalDataTracking({ userId, optOutDate }));
            },
            unchecked: () => {
              dispatch(GDPRActions.updatePersonalDataTracking({ userId, optOutDate }));
            },
          },
        },
        requestForget: {
          heading: {
            checked:
              'Your Personal Data is about to be disassociated from your account. This action will forfeit your licenses and access to all FlowJo systems. Are you sure you want to continue?',
          },
          callback: {
            // @TODO wire in callback to delete user
            checked: () => {
              // `logout` is not part of the typed AuthClientUtils surface; preserve the
              // original (untyped-JS) call exactly by narrowing at this boundary.
              (AuthClientUtils as unknown as { logout: (s: unknown) => void }).logout(store);
              navigate('/');
            },
          },
        },
        portData: {
          heading: {
            checked:
              'Your Personal Data is about to be to be sent from your account to another Data Protection Officer. This action may expose your data to other data owners. Are you sure you want to continue?',
          },
          callback: {
            checked: () => navigate('/port-data'),
          },
        },
      };

      const heading: string = get(
        hash,
        `${name}.heading.${checked ? 'checked' : 'unchecked'}`,
        'Are you sure?',
      );
      const callback: () => void = get(
        hash,
        `${name}.callback.${checked ? 'checked' : 'unchecked'}`,
        () => {},
      );
      return { heading, callback };
    },
    [currentUser, dispatch, store],
  );

  const revertUpdate = useCallback((name: CheckName) => {
    setChecks((prev) => ({ ...prev, [name]: !prev[name] }));
  }, []);

  const confirmUpdate = useCallback(
    (name: CheckName, checked: boolean) => {
      const modal = setupConfirmationModal(name, checked);

      confirm({
        heading: modal.heading,
        accept: {
          text: 'Yes',
          callback: modal.callback,
        },
        cancel: {
          text: 'Cancel',
          callback: () => revertUpdate(name),
        },
      });
    },
    [confirm, revertUpdate, setupConfirmationModal],
  );

  const onValueChange = useCallback(
    ({ target: { name, checked } }: React.ChangeEvent<HTMLInputElement>) => {
      const checkName = name as CheckName;
      setChecks((prev) => ({ ...prev, [checkName]: checked }));
      confirmUpdate(checkName, checked);
    },
    [confirmUpdate],
  );

  const closeModal = useCallback(() => navigate('/'), []);

  const renderGDPRResult = (): React.JSX.Element => (
    <Modal
      size={fjModalSizes.SM}
      accept={{
        text: 'Ok',
        callback: () => {
          // The creator defaults the omitted fields to `undefined`, so passing only
          // `{ open: false }` yields the identical action; cast to satisfy the arg type.
          dispatch(
            GDPRActions.updatePersonalDataTrackingResult({ open: false } as Parameters<
              typeof GDPRActions.updatePersonalDataTrackingResult
            >[0]),
          );
        },
      }}
      title={gdprResult.result}
    />
  );

  return (
    <React.Fragment>
      <Modal
        cancel={{
          text: 'Cancel',
          callback: closeModal,
        }}
        blocking
        title="Settings"
        size={fjModalSizes.LG}
      >
        <ul className="list-style-none padding-0">
          <li key="personal-data" className="margin-bottom-med" data-test="personal-data">
            <FjCheckbox
              type="checkbox"
              label="Store Personal Data"
              checked={checks.personalData}
              name="personalData"
              onChange={onValueChange}
            />
          </li>
          <li key="request-forget" className="margin-bottom-med" data-test="request-forget">
            <FjCheckbox
              type="checkbox"
              label="Request to Forget"
              checked={checks.requestForget}
              name="requestForget"
              onChange={onValueChange}
            />
          </li>
          <li key="port-data" className="margin-bottom-med" data-test="port-data">
            <FjCheckbox
              type="checkbox"
              label="Port Personal Data"
              checked={checks.portData}
              name="portData"
              onChange={onValueChange}
            />
          </li>
        </ul>
      </Modal>
      {gdprResult.open && renderGDPRResult()}
    </React.Fragment>
  );
};

export default Settings;
